import {
  IframeToParentCommunicator,
  IIframeToParentCommunicator
} from '@/pages/home/utils/CrossWindowCommunicator';
import {
  DatalabPostMessageType,
  TDatalabCommunicatorData,
} from "@/pages/datalab/pages/IframePage/types";

type StrictPlainObject = {
  [key: string]: unknown;
} & {
  constructor: Function;  // 确保是 Object 构造的
};

/**
 * Datalab到报表的通信器接口
 * 扩展了基础的iframe到父窗口通信器接口，添加了Datalab特定的通信功能
 */
export interface IDatalabToReportCommunicator extends IIframeToParentCommunicator {
  /** token更新事件类型 */
  readonly updatedTokenEventType: string;
  /** 当前token */
  token: string | undefined;
  /** 报表ID */
  dashboardId: string | undefined;
  /** 报表的名称 */
  dashboardName: string | undefined;
  /** 报表所在的业务 */
  gameCode: string | undefined;
  /** 域名地址 */
  origin: string | undefined;
  
  /**
   * 保存和Datalab通信数据
   * @param data 要保存的通信数据
   */
  saveDatalabCommunicatorData(data: TDatalabCommunicatorData): void;
  
  /**
   * 通知父窗口 iframe内部组件已挂载
   */
  notifyComponentMounted(): void;
  
  /**
   * 通知父窗口用户点击了探索按钮
   */
  notifyExploreButtonClick(payload: any): void;
  
  /**
   * 通知父窗口需要刷新token
   */
  notifyRefreshToken(): void;
  
  /**
   * 处理token更新
   * @param payload token更新负载
   */
  handleTokenUpdate(payload: TDatalabCommunicatorData): void;
}

/**
 * Datalab到报表的通信器实现类
 * 实现了单例模式，用于管理iframe与父窗口之间的通信
 */
export default class DatalabToReportCommunicator extends IframeToParentCommunicator implements IDatalabToReportCommunicator {
  /** 单例实例 */
  private static instance: DatalabToReportCommunicator | null = null;
  
  /**
   * 获取通信器单例实例
   * @returns 通信器实例
   */
  public static getInstance(): DatalabToReportCommunicator {
    if (!DatalabToReportCommunicator.instance) {
      DatalabToReportCommunicator.instance = new DatalabToReportCommunicator();
    }
    return DatalabToReportCommunicator.instance;
  }
  
  /**
   * 销毁通信器单例实例
   */
  public static destroyInstance(): void {
    if (DatalabToReportCommunicator.instance) {
      DatalabToReportCommunicator.instance.destroy(); // 销毁实例的注册事件
      DatalabToReportCommunicator.instance = null;
    }
  }
  
  /** token更新事件类型常量 */
  private readonly _updatedTokenEventType = 'updatedToken';
  /** 承接页面的报表id */
  private _dashboardId: string | undefined;
  /** 当前承接页面报表的名称 */
  private _dashboardName: string | undefined;
  /** 当前承接页面报表的所在业务 */
  private _gameCode: string | undefined;
  /** 承接页面传递过来的其余参数, 普通对象 */
  private _paramsOfIframe: StrictPlainObject;
  /** 域名地址 */
  private _origin: string | undefined;
  /** 当前token */
  private _token: string | undefined;
  
  constructor() {
    super();
    // 配置允许的通信来源
    // nginx.conf文件已配置
    // this.configAllowedOrigins(ALLOWED_COMMON_ORIGIN);
  }
  
  public get updatedTokenEventType(): string {
    return this._updatedTokenEventType;
  }
  
  public get token(): string | undefined {
    return this._token;
  }
  
  public get dashboardId(): string | undefined {
    return this._dashboardId;
  }
  
  public get dashboardName(): string | undefined {
    return this._dashboardName;
  }
  
  public get gameCode(): string | undefined {
    return this._gameCode;
  }
  
  public get paramsOfIframe(): StrictPlainObject {
    return this._paramsOfIframe;
  }
  
  public get origin(): string | undefined {
    return this._origin;
  }
  
  /**
   * 保存Datalab通信数据
   * @param data 要保存的通信数据
   */
  public saveDatalabCommunicatorData(data: TDatalabCommunicatorData): void {
    this._token = data?.token;
    this._dashboardId = data?.dashboardId;
    this._dashboardName = data?.dashboardName;
    this._gameCode = data?.gameCode;
    this._paramsOfIframe = data?.paramsOfIframe;
    this._origin = data?.origin;
  }
  
  /**
   * 通知父窗口组件已挂载
   */
  public notifyComponentMounted(): void {
    this.sendToParent(DatalabPostMessageType.ComponentMounted);
  }
  
  /**
   * 通知父窗口用户点击了探索按钮
   */
  public notifyExploreButtonClick(payload: any): void {
    this.sendToParent(DatalabPostMessageType.ClickExplore, {...payload});
  }
  /**
   * 通知父窗口探索图表页面返回报表承接页面
   */
  public notifyGoBack(): void {
    this.sendToParent(DatalabPostMessageType.GoBackReport);
  }
  
  /**
   * 通知父窗口用户点击了表格放大或缩小
   * operation: 'notFullScreen' 缩小; fullscreen 放大
   */
  public notifyTableFullscreenClick(payload: any): void {
    this.sendToParent(DatalabPostMessageType.ClickFullscreen, {...payload});
  }
  
  /**
   * 通知父窗口需要刷新token
   */
  public notifyRefreshToken(): void {
    this.sendToParent(DatalabPostMessageType.RefreshToken);
  }
  
  /**
   * 处理token更新
   * @param payload 包含更新后的数据或错误信息
   */
  public handleTokenUpdate(payload: TDatalabCommunicatorData) {
    if (!payload.code) {
      // 保存有效数据
      this.saveDatalabCommunicatorData(payload);
    }
    // 触发token更新事件
    this.emit(this._updatedTokenEventType, payload);
  }
}